import { Fragment, ReactNode, useCallback, memo } from 'react';

import bytes from 'bytes';
import { FormattedMessage } from 'react-intl';
import Truncate from 'react-truncate';

import Avatar from '@atlaskit/avatar';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import DecisionIcon from '@atlaskit/icon/core/decision';
import { Box } from '@atlaskit/primitives';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';
import { RelativeDate } from '@atlassian/bitkit-date';

import Announcer from 'src/components/announcer/src/announcer';
import PageableTable, {
  TableCell,
  TableHeaderMobileHidden,
  TableCellMobileHidden,
} from 'src/components/pageable-table';
import RepositoryBuildStatus from 'src/components/repository-table/repository-build-status';
import { Repository } from 'src/components/types';
import { useIntl } from 'src/hooks/intl';
import { useStatsigGate } from 'src/hooks/statsig';
import { Link } from 'src/router/components';
import EmptyResults from 'src/sections/workspace/components/empty-results';
import { getPagination } from 'src/utils/get-pagination';

import { WorkspaceRepositoryPagination } from '../containers/workspace-repository-pagination';

import * as styles from './profile-repositories-table.style';
import { messages } from './profile-repositories.i18n';

type SortableHeaderProps = {
  field: SortField;
  currentSortField: SortField;
  isAscending: boolean;
  onSortChange: (field: SortField) => void;
  children: ReactNode;
};

const SortableHeader = ({
  field,
  currentSortField,
  isAscending,
  onSortChange,
  children,
}: SortableHeaderProps) => {
  const isActive = currentSortField === field;

  return (
    <span
      onClick={() => onSortChange(field)}
      style={{
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
      }}
      role="button"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') onSortChange(field);
      }}
    >
      {children}
      <Box
        paddingInlineStart="space.050"
        style={{ display: 'inline-block', opacity: 1 }}
      >
        {isActive ? (
          isAscending ? (
            <ChevronUpIcon label="Ascending" size="small" />
          ) : (
            <ChevronDownIcon label="Descending" size="small" />
          )
        ) : (
          <ChevronDownIcon label="Sort descending" size="small" />
        )}
      </Box>
    </span>
  );
};

export type SortField = 'updated_on' | 'size' | 'name';

export type Props = {
  repositories: Repository[];
  pagelen: number | undefined;
  size: number | undefined;
  isLoading: boolean;
  isError: boolean;
  sentryId?: string | undefined;
  getPaginationUrl: (selectedPage: number) => string;
  page: number | null;
  isAscending: boolean;
  handleSortChange: (field: SortField) => void;
  sortField: SortField;
  isFilterApplied: boolean;
  workspaceSlug: string;
  createUrl: string | undefined;
  importUrl: string | undefined;
  buildStatusesFetched: boolean;
};

export const ProfileRepositoriesTable = memo<Props>(
  ({
    repositories,
    pagelen,
    size,
    isLoading,
    isError,
    sentryId,
    getPaginationUrl,
    page,
    isAscending,
    handleSortChange,
    sortField,
    isFilterApplied,
    createUrl,
    importUrl,
    buildStatusesFetched,
  }) => {
    const intl = useIntl();

    const noNewXWorkspaceForks = useStatsigGate(
      StatsigFeatureKeys.noNewXWorkspaceForks
    );

    const renderHeaders = useCallback(
      (): JSX.Element => (
        <tr>
          <styles.RepositoryInfoTableHeader width="28%">
            <SortableHeader
              field="name"
              currentSortField={sortField}
              isAscending={isAscending}
              onSortChange={handleSortChange}
            >
              <FormattedMessage {...messages.tableHeaderRepository} />
            </SortableHeader>
          </styles.RepositoryInfoTableHeader>
          <TableHeaderMobileHidden width="45%">
            <FormattedMessage {...messages.tableHeaderDescription} />
          </TableHeaderMobileHidden>
          <TableHeaderMobileHidden
            width="10%"
            aria-sort={
              sortField === 'size'
                ? isAscending
                  ? 'ascending'
                  : 'descending'
                : undefined
            }
          >
            <SortableHeader
              field="size"
              currentSortField={sortField}
              isAscending={isAscending}
              onSortChange={handleSortChange}
            >
              <FormattedMessage {...messages.tableHeaderSize} />
            </SortableHeader>
          </TableHeaderMobileHidden>
          <TableHeaderMobileHidden
            width={`${16 * 8}px`}
            aria-sort={
              sortField === 'updated_on'
                ? isAscending
                  ? 'ascending'
                  : 'descending'
                : undefined
            }
          >
            <SortableHeader
              field="updated_on"
              currentSortField={sortField}
              isAscending={isAscending}
              onSortChange={handleSortChange}
            >
              <FormattedMessage {...messages.tableHeaderLastUpdated} />
            </SortableHeader>
          </TableHeaderMobileHidden>
          <styles.BuildStatusTableHeader width="64px" textAlign="center">
            <FormattedMessage {...messages.tableHeaderBuilds} />
          </styles.BuildStatusTableHeader>
        </tr>
      ),
      [sortField, isAscending, handleSortChange]
    );

    // @ts-ignore TODO: fix noImplicitAny error here
    const getLastUpdatedDate = useCallback(
      (repository: Repository): JSX.Element | null => {
        const { uuid, updated_on: updated, created_on: created } = repository;
        return updated ? (
          <RelativeDate key={`date-key-${uuid}`} date={new Date(updated)} />
        ) : created ? (
          <RelativeDate key={`date-key-${uuid}`} date={new Date(created)} />
        ) : null;
      },
      []
    );

    const renderRow = useCallback(
      (repository: Repository): JSX.Element => {
        const { uuid, name, links, slug, project, size: repoSize } = repository;
        const date = getLastUpdatedDate(repository);
        const projectLink =
          project && project.links ? project.links.html.href : null;
        return (
          <styles.RepositoryRow key={uuid} data-testid="profile-repository-row">
            <TableCell widthWithUnit="28%">
              <styles.RepositoryContainer>
                <Box>
                  <Avatar
                    size="medium"
                    appearance="square"
                    name={name}
                    src={links.avatar.href}
                    status={repository.is_private ? 'locked' : null}
                    href={links.html.href}
                  />
                </Box>
                <styles.RepositoryInfo>
                  <styles.RepositoryLink to={links.html.href}>
                    {slug}
                  </styles.RepositoryLink>
                  <styles.ProjectNameUpdatedContainer>
                    {!!project && !!project.name && !!projectLink && (
                      <styles.Info>
                        <Link to={projectLink}>{project.name}</Link>
                      </styles.Info>
                    )}
                    {noNewXWorkspaceForks && repository.parent ? (
                      <styles.ForkInfo>
                        • <DecisionIcon size="small" label="fork icon" />{' '}
                        <FormattedMessage {...messages.forkOfLabel} />{' '}
                        <Link to={repository.parent.links.html.href}>
                          {repository.parent.name}
                        </Link>
                      </styles.ForkInfo>
                    ) : null}
                    <styles.DateWrapper>
                      {`• `}
                      <FormattedMessage {...messages.updatedLabel} /> {date}
                    </styles.DateWrapper>
                  </styles.ProjectNameUpdatedContainer>
                </styles.RepositoryInfo>
              </styles.RepositoryContainer>
            </TableCell>
            <TableCellMobileHidden widthWithUnit="45%">
              <styles.DescriptionWrapper>
                <Truncate lines={2}>{repository.description}</Truncate>
              </styles.DescriptionWrapper>
            </TableCellMobileHidden>
            <TableCellMobileHidden widthWithUnit="10%">
              {repoSize || repoSize === 0
                ? bytes(repoSize, { unitSeparator: ' ', decimalPlaces: 1 })
                : ''}
            </TableCellMobileHidden>
            <TableCellMobileHidden widthWithUnit="128px">
              {date}
            </TableCellMobileHidden>
            <TableCell centered widthWithUnit="64px">
              <RepositoryBuildStatus repository={repository} />
            </TableCell>
          </styles.RepositoryRow>
        );
      },
      [getLastUpdatedDate, noNewXWorkspaceForks]
    );

    const renderEmptyState = useCallback((): JSX.Element => {
      const showEmptyState =
        repositories?.length === 0 &&
        !(isLoading || isError) &&
        buildStatusesFetched;

      if (!showEmptyState) {
        return <Fragment />;
      }

      const emptyResultsProps = isFilterApplied
        ? {
            header: messages.noMatchHeader,
            desc: messages.noMatchDesc,
            primaryAction: {
              msg: messages.clearAllFilters,
              href: window.location.pathname,
            },
          }
        : {
            header: messages.noReposHeader,
            desc: messages.noReposDesc,
            primaryAction: createUrl
              ? {
                  msg: messages.createLink,
                  href: createUrl,
                }
              : undefined,
            secondaryAction: importUrl
              ? {
                  msg: messages.importLink,
                  href: importUrl,
                }
              : undefined,
          };

      return (
        <tr>
          <td colSpan={5}>
            <EmptyResults {...emptyResultsProps} />
          </td>
        </tr>
      );
    }, [
      repositories,
      buildStatusesFetched,
      isLoading,
      isError,
      isFilterApplied,
      createUrl,
      importUrl,
    ]);

    const renderAnnouncerMessage = useCallback(() => {
      if (isLoading) {
        if (isFilterApplied) {
          return intl.formatMessage(messages.announceFilteringProcess);
        }
        return intl.formatMessage(messages.announceLoading);
      }

      // `repositories` is the list of repositories and `size` is the
      // total number of repositories in the list for the given workspace.
      const repositoriesCount = repositories ? repositories.length : 0;

      const hasPages = size && pagelen ? size / pagelen > 1 : false;

      if (isFilterApplied && !isLoading) {
        if (repositoriesCount) {
          return intl.formatMessage(
            hasPages
              ? messages.announcePaginatedFilteringRepositories
              : messages.announceFilteringRepositories,
            { count: repositoriesCount }
          );
        }
        return intl.formatMessage(
          messages.announceFilteringNoRepositoriesFound
        );
      }

      return intl.formatMessage(messages.announceDisplayingAllRepositories);
    }, [isLoading, isFilterApplied, intl, repositories, size, pagelen]);

    const totalPages = size && pagelen ? Math.ceil(size / pagelen) : 1;
    const pages = getPagination(totalPages)(getPaginationUrl);

    const tableProps = {
      isLoading,
      isError,
      sentryId,
      rows: repositories,
      renderHeaders,
      renderEmptyState,
      renderPagination: () => (
        <styles.PaginationContainer>
          <WorkspaceRepositoryPagination
            page={page ? Math.max(page, 1) : 1}
            isLoading={isLoading}
            isError={isError}
            pages={pages}
            uiEventSource="WorkspaceRepositoriesScreen"
            uiEventSubjectId="WorkspaceRepositoriesPagination"
          />
        </styles.PaginationContainer>
      ),
      children: renderRow,
      renderAnnouncer: () => (
        <Announcer text={renderAnnouncerMessage()} delay={200} />
      ),
    };

    return <PageableTable {...tableProps} />;
  }
);
